import { useQuery } from '@tanstack/react-query';
import { buildAuthenticatedHeaders } from './authStorage.ts';
import type { BillingPipelineRecord, BillingStageFilter } from './apiContract.ts';
import type { MvpInvoiceRecord } from './mvpApi.ts';
export type { BillingPipelineItem, BillingPipelineRecord, BillingStage, BillingStageFilter } from './apiContract.ts';

export type BillingPipelineView = BillingStageFilter;

export const BILLING_QUERY_KEY_ROOT = ['field-service-technique-billing'];

function getBillingApiBaseUrl(): string {
  const raw = String(import.meta.env.VITE_DEMO_API_BASE_URL || '/api').trim();
  if (!raw) return '/api';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

function buildSearchParams(view: BillingPipelineView): string {
  if (!view || view === 'queue') return '';
  return `?stage=${encodeURIComponent(view)}`;
}

async function requestBillingJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${getBillingApiBaseUrl()}${path}`, {
    signal,
    credentials: 'include',
    headers: buildAuthenticatedHeaders({
      Accept: 'application/json',
    }),
  });

  const contentType = response.headers.get('content-type') || '';
  if (!response.ok) {
    let detail = response.statusText;
    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as { detail?: string; error?: string };
      detail = payload.detail || payload.error || detail;
    } else {
      detail = (await response.text()) || detail;
    }
    throw new Error(`HTTP ${response.status}: ${detail}`);
  }

  if (!contentType.includes('application/json')) {
    throw new Error(`Type de contenu inattendu renvoyé par l’API de facturation : ${contentType || 'inconnu'}`);
  }

  return (await response.json()) as T;
}

export async function fetchBillingPipeline(
  view: BillingPipelineView = 'queue',
  signal?: AbortSignal,
): Promise<BillingPipelineRecord[]> {
  return requestBillingJson<BillingPipelineRecord[]>(`/app/billing/pipeline${buildSearchParams(view)}`, signal);
}

export function useBillingPipelineData(enabled: boolean, view: BillingPipelineView = 'queue') {
  const pipelineQuery = useQuery({
    queryKey: [...BILLING_QUERY_KEY_ROOT, 'pipeline', view],
    queryFn: ({ signal }) => fetchBillingPipeline(view, signal),
    enabled,
    staleTime: 15_000,
    retry: false,
  });

  return {
    pipeline: (pipelineQuery.data as BillingPipelineRecord[] | null | undefined) ?? [],
    items: (pipelineQuery.data as BillingPipelineRecord[] | null | undefined) ?? [],
    isLoading: pipelineQuery.isLoading,
    error: pipelineQuery.error,
  };
}

export async function fetchMvpInvoices(signal?: AbortSignal): Promise<MvpInvoiceRecord[]> {
  return requestBillingJson<MvpInvoiceRecord[]>('/mvp/invoices', signal);
}

export function useMvpInvoicesData(enabled: boolean) {
  const invoicesQuery = useQuery({
    queryKey: [...BILLING_QUERY_KEY_ROOT, 'mvp-invoices'],
    queryFn: ({ signal }) => fetchMvpInvoices(signal),
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  return {
    invoices: (invoicesQuery.data as MvpInvoiceRecord[] | null | undefined) ?? [],
    isLoading: invoicesQuery.isLoading,
    error: invoicesQuery.error,
  };
}
